import React, { useEffect, useState } from 'react';
import ApiClient from '../../utils/ApiClient';
import { isAdmin } from '../../auth';

const NUMBER_FIELDS = [
  ['days', 'Days of rank-based sales split across the warehouses'],
  ['minUnits', 'Minimum units per warehouse'],
  ['bbLookbackDays', 'Buy Box lookback (days)'],
  ['normalBbPct', 'Normal Buy Box % (full rate at or above)'],
  ['violatorMinDays', 'MAP violator days that trigger the reduced rate'],
  ['lowDays', 'Running low below (days of cover in a region)'],
  ['criticalDays', 'Critical below (days of cover in a region)'],
  ['minMonthlySalesForWarnings', 'Minimum sales per month for low/critical warnings'],
  ['roleMinUnits', 'Units received before a warehouse gets a hub/keeper role'],
  ['hubMovedPct', 'Hub: % of received units moved on (at or above)'],
  ['keeperMovedPct', 'Keeper: % of received units moved on (at or below)'],
  ['speedLossMinPct', "Losing on speed: % of a region's checks slower or tied (at or above)"],
  ['speedLossMinChecks', 'Losing on speed: minimum checks in a region'],
  ['hubStickyMinUnits', "Hub stickiness: units dispersed before a hub's own share is used"],
  // Spec 2026-11-03 §9 (coverage sizing). shareMinOrders is retired: no row, but it is sent back as loaded.
  ['rankCalibration', 'Rank calibration (× rank-implied rate)', 'any'],
  ['rankWindowDays', 'Rank averaging window (days; 1 = latest rank only)', '1'],
  ['overstockDays', 'Overstock: days of cover above which a low-Buy-Box product is not reordered'],
  ['overstockBbPct', 'Overstock: Buy Box % below which the guard applies', 'any'],
];

const WEIGHT_TOLERANCE = 0.5;
const weightSum = (w) => Object.values(w || {}).reduce((a, b) => a + (Number(b) || 0), 0);

const RATING_FIELDS = [
  ['keeperCriticalOrOut', 'Keeper in a critical or out region'],
  ['keeperNewShortRegion', 'Keeper, new warehouse, region short'],
  ['keeperHeldLowRegion', 'Keeper already held, region running low'],
  ['keeperNewFullRegion', 'Keeper, new warehouse, region at target'],
  ['keeperLosingSpeedRegion', 'Keeper in a region losing on speed'],
  ['mixedFactor', 'Mixed warehouse (share of keeper rating)'],
  ['keeperHeldHealthy', 'Keeper already held, region healthy'],
  ['unknown', 'Warehouse with no role yet'],
];

export default function SpreadSettingsModal({ open, onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [meta, setMeta] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const canEdit = isAdmin();

  useEffect(() => {
    if (!open) return;
    setError(null);
    ApiClient.get('/api/po-creation/spread-settings')
      .then(({ data }) => { setS(data.settings); setMeta({ by: data.updatedBy, at: data.updatedAt }); })
      .catch(() => setError('Could not load spread settings'));
  }, [open]);

  if (!open) return null;
  const target = s ? Object.values(s.perRegion).reduce((a, b) => a + (Number(b) || 0), 0) : 0;
  const setField = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    const bad = [];
    const toNum = (v, label) => {
      const n = typeof v === 'number' ? v : (String(v ?? '').trim() === '' ? NaN : Number(v));
      if (!Number.isFinite(n)) { bad.push(label); return v; }
      return n;
    };
    const out = { ...s };
    out.perRegion = {};
    Object.keys(s.perRegion).forEach((r) => { out.perRegion[r] = toNum(s.perRegion[r], `${r} warehouses`); });
    NUMBER_FIELDS.forEach(([k, label]) => { if (s[k] !== undefined) out[k] = toNum(s[k], label); });
    if (s.ratings) {
      out.ratings = { ...s.ratings };
      RATING_FIELDS.forEach(([k, label]) => { if (k in s.ratings) out.ratings[k] = toNum(s.ratings[k], label); });
    }
    if (s.regionWeights) {
      out.regionWeights = {};
      Object.keys(s.regionWeights).forEach((r) => { out.regionWeights[r] = toNum(s.regionWeights[r], `${r} weight %`); });
    }
    if (bad.length) { setError(`Enter a number for: ${bad.join(', ')}`); return; }
    const negative = Object.entries(out.regionWeights || {}).filter(([, w]) => w < 0).map(([r]) => r);
    if (negative.length) { setError(`Region weights cannot be negative: ${negative.join(', ')}`); return; }
    if (out.regionWeights && Math.abs(weightSum(out.regionWeights) - 100) > WEIGHT_TOLERANCE) {
      setError(`Region weights must add up to 100 (± ${WEIGHT_TOLERANCE}); they add up to ${weightSum(out.regionWeights).toFixed(1)}`);
      return;
    }
    setSaving(true); setError(null);
    try {
      await ApiClient.put('/api/po-creation/spread-settings', out);
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      setError(e?.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-label="Spread settings">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-5 text-gray-900 shadow-xl dark:bg-gray-800 dark:text-gray-100">
        <h2 className="mb-1 text-lg font-semibold">Spread settings</h2>
        {meta.by && <p className="mb-3 text-xs text-gray-500">Last changed by {meta.by} · {meta.at}</p>}
        {!s ? <p className="text-sm">Loading…</p> : (
          <div className="space-y-3 text-sm">
            <div>
              <p className="mb-1 font-medium">Warehouses per region — target: {target} warehouses</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(s.perRegion).map((r) => (
                  <label key={r} className="flex items-center justify-between gap-2" title={(s.regions[r] || []).join(' ')}>
                    <span>{r}</span>
                    <input aria-label={`${r} warehouses`} type="number" min="0" max="10" disabled={!canEdit}
                      className="w-16 rounded border px-1 py-0.5 dark:bg-gray-700"
                      value={s.perRegion[r]}
                      onChange={(e) => setField('perRegion', { ...s.perRegion, [r]: e.target.value })} />
                  </label>
                ))}
              </div>
            </div>
            {NUMBER_FIELDS.map(([k, label, step]) => (
              <label key={k} className="flex items-center justify-between gap-2">
                <span>{label}</span>
                <input aria-label={label} type="number" step={step} disabled={!canEdit}
                  className="w-20 rounded border px-1 py-0.5 dark:bg-gray-700"
                  value={s[k] ?? ''} onChange={(e) => setField(k, e.target.value)} />
              </label>
            ))}
            {s.regionWeights && (() => {
              const sum = weightSum(s.regionWeights);
              const off = Math.abs(sum - 100) > WEIGHT_TOLERANCE;
              return (
                <div>
                  <p className="mb-1 font-medium">
                    Region weights (%) —{' '}
                    <span data-testid="weights-sum" className={off ? 'text-red-600' : 'text-gray-600 dark:text-gray-300'}>
                      Sum {sum.toFixed(1)}{off ? ' (must be 100)' : ''}
                    </span>
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(s.regionWeights).map((r) => (
                      <label key={r} className="flex items-center justify-between gap-2">
                        <span>{r}</span>
                        <input aria-label={`${r} weight %`} type="number" min="0" max="100" step="any" disabled={!canEdit}
                          className="w-16 rounded border px-1 py-0.5 dark:bg-gray-700"
                          value={s.regionWeights[r]}
                          onChange={(e) => setField('regionWeights', { ...s.regionWeights, [r]: e.target.value })} />
                      </label>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Share of national demand each region is sized for (rank-implied rate × weight × horizon).</p>
                </div>
              );
            })()}
            <div>
              <p className="mb-1 font-medium">Placement ratings (Better / No change / Worse)</p>
              {RATING_FIELDS.map(([k, label]) => (
                <label key={k} className="flex items-center justify-between gap-2">
                  <span>{label}</span>
                  <input aria-label={label} type="number" step="0.5" disabled={!canEdit}
                    className="w-20 rounded border px-1 py-0.5 dark:bg-gray-700"
                    value={s.ratings?.[k] ?? ''}
                    onChange={(e) => setField('ratings', { ...s.ratings, [k]: e.target.value })} />
                </label>
              ))}
            </div>
            {[['roundToBox', 'Round each warehouse up to a full box'],
              ['includeOversize', 'Include oversize products'],
              ['countInboundAsHeld', 'Count warehouses with stock inbound as held']].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2">
                <input aria-label={label} type="checkbox" disabled={!canEdit} checked={!!s[k]}
                  onChange={(e) => setField(k, e.target.checked)} />
                <span>{label}</span>
              </label>
            ))}
            <p className="text-xs text-gray-500">Units per warehouse = rank-based sales/day × days ÷ target, rounded up, then to a full box. Rank → sales: calibrate_fba_toolkit_velocity_data.</p>
          </div>
        )}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button className="rounded border px-3 py-1" onClick={onClose}>Close</button>
          {canEdit && <button className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50" disabled={saving || !s} onClick={save}>Save</button>}
        </div>
      </div>
    </div>
  );
}
